import { LEADER_TIMEOUT_MS, type EffectiveChatKeybinds, type WebKeybindAction } from '../../../packages/plugin-shared/chatKeys.mjs';

export type WebChatAction = Exclude<WebKeybindAction, 'leader'>;
type Resolution = WebChatAction | 'leader' | 'cancel' | null;

function chordId(event: KeyboardEvent): string {
  const modifiers = [event.ctrlKey && 'ctrl', event.shiftKey && 'shift', event.altKey && 'alt', event.metaKey && 'super'].filter(Boolean);
  return [...modifiers, event.key.toLowerCase()].join('+');
}

/** Native browser event adapter. The CLI owns its terminal decoder; grammar/defaults are shared. */
export function createChatKeymap(onPending: (pending: boolean) => void, effective: EffectiveChatKeybinds<'web'>) {
  if (effective.issues.length) throw new Error(`Invalid web keymap: ${effective.issues.map(issue => issue.message).join('; ')}`);
  const bindings = effective.actions.flatMap(action => (effective.bindings.get(action) ?? []).map(binding => ({ action, ...binding })));
  let pending = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cancel = () => {
    clearTimeout(timer);
    timer = undefined;
    if (pending) { pending = false; onPending(false); }
  };
  return {
    pending: () => pending,
    cancel,
    resolve(event: KeyboardEvent, selected: boolean): Resolution {
      if (event.isComposing || event.metaKey) return null;
      const id = chordId(event);
      const direct = bindings.find(binding => !binding.leader && binding.chord.id === id)?.action;
      if (event.repeat) return pending || (direct && !(direct === 'leader' && selected)) ? 'cancel' : null;
      if (pending) {
        // Modifier presses are not the second chord.
        if (['Control', 'Shift', 'Alt', 'Meta'].includes(event.key)) return null;
        cancel();
        return bindings.find(binding => binding.leader && binding.chord.id === id)?.action as WebChatAction | undefined ?? 'cancel';
      }
      const action = direct;
      if (action === 'leader') {
        if (selected) return null;
        pending = true;
        onPending(true);
        timer = setTimeout(cancel, LEADER_TIMEOUT_MS);
      }
      return action ?? null;
    },
  };
}

/** Open dialogs own all shortcuts. Only the composer is an editable keyboard target. */
export function chatKeyTarget(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof HTMLElement) || !target.closest('[data-chat-keyboard-surface]')) return null;
  if (target.closest('[role="dialog"], [role="menu"], [role="listbox"]') || document.querySelector('[role="dialog"][aria-modal="true"]')) return null;
  if (target.closest('input, select, [contenteditable="true"]')) return null;
  if (target.closest('textarea') && !target.matches('[data-testid="chat-composer"]')) return null;
  return target;
}
